import { tracked } from '@glimmer/tracking';

import { ApiMetricBuilderWizardStep } from 'webapp/enums/enums';
import { prettyJsonStringify } from 'webapp/utils/common';

import type { RowType } from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/json-structure/row';
import JSONStructureRow from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/json-structure/row';

enum OutputPayloadKey {
	METRIC = 'value',
	DATE = 'date',
	DIMENSION = 'dimension',
}

export default class JSONStructure {
	@tracked rowMetric: JSONStructureRow | null = null;
	@tracked rowDate: JSONStructureRow | null = null;
	@tracked rowDimension: JSONStructureRow | null = null;

	// these are used in wizard step-common to either preselect first available row (when false)
	// or show these already selected (when true)
	private hasRowMetricBeenUpdated = false;
	private hasRowDateBeenUpdated = false;
	private hasRowDimensionBeenUpdated = false;

	json: SomeDataObject;
	jsonString: string;
	rows: JSONStructureRow[] = [];

	constructor(json: SomeDataObject) {
		this.json = json;
		this.jsonString = prettyJsonStringify(this.json);
		this.createRows();
	}

	getRowForWizardStep(type: RowType): JSONStructureRow | null {
		switch (type) {
			case ApiMetricBuilderWizardStep.METRIC:
				return this.rowMetric;
			case ApiMetricBuilderWizardStep.DIMENSION:
				return this.rowDimension;
			case ApiMetricBuilderWizardStep.DATE:
				return this.rowDate;
		}
	}

	setRowForWizardStep(type: RowType, row: JSONStructureRow | null): void {
		switch (type) {
			case ApiMetricBuilderWizardStep.METRIC:
				this.rowMetric = row;
				this.hasRowMetricBeenUpdated = true;
				break;
			case ApiMetricBuilderWizardStep.DIMENSION:
				this.rowDimension = row;
				this.hasRowDimensionBeenUpdated = true;
				break;
			case ApiMetricBuilderWizardStep.DATE:
				this.rowDate = row;
				this.hasRowDateBeenUpdated = true;
				break;
		}
	}

	hasRowForWizardStepBeenUpdated(type: RowType): boolean {
		switch (type) {
			case ApiMetricBuilderWizardStep.METRIC:
				return this.hasRowMetricBeenUpdated;
			case ApiMetricBuilderWizardStep.DIMENSION:
				return this.hasRowDimensionBeenUpdated;
			case ApiMetricBuilderWizardStep.DATE:
				return this.hasRowDateBeenUpdated;
		}
	}

	getFirstAvailableRow(type: RowType): JSONStructureRow | null {
		const rows: JSONStructureRow[] = this.rows.filter((item) => item.isValid) || [];

		let bestRow: JSONStructureRow | null = null;
		rows.some((row) => {
			if (row.checkValidityForDataType(type, true)) {
				// don't select object keys for dimensions
				if (type !== ApiMetricBuilderWizardStep.DIMENSION || !row.isObjectKey) {
					bestRow = row;
					return true;
				}
			}
			return false;
		});
		// autoselect first valid only for metric
		if (!bestRow && rows[0] && type === ApiMetricBuilderWizardStep.METRIC) {
			bestRow = rows[0];
		}

		return bestRow || null;
	}

	generateJsonAta(): string {
		let string = '';

		interface RowTypeAndModel {
			type: ApiMetricBuilderWizardStep;
			model: JSONStructureRow;
		}

		const rowMetric = this.rowMetric,
			rowDate = this.rowDate,
			rowDimension = this.rowDimension,
			rows: RowTypeAndModel[] = [];

		const getOutputPayloadKeyForRowType = (
			rowType: ApiMetricBuilderWizardStep,
		): OutputPayloadKey => {
			switch (rowType) {
				case ApiMetricBuilderWizardStep.METRIC:
					return OutputPayloadKey.METRIC;
				case ApiMetricBuilderWizardStep.DATE:
					return OutputPayloadKey.DATE;
				case ApiMetricBuilderWizardStep.DIMENSION:
					return OutputPayloadKey.DIMENSION;
				default:
					return OutputPayloadKey.METRIC;
			}
		};

		if (rowMetric) {
			rows.push({
				type: ApiMetricBuilderWizardStep.METRIC,
				model: rowMetric,
			});
		}
		if (rowDate) {
			rows.push({
				type: ApiMetricBuilderWizardStep.DATE,
				model: rowDate,
			});
		}
		if (rowDimension) {
			rows.push({
				type: ApiMetricBuilderWizardStep.DIMENSION,
				model: rowDimension,
			});
		}

		// we only support one of this kind
		const rowThatIsObjectKey = rows.find((rowData) => rowData.model.isObjectKey);

		const isResultArray =
			this.rowMetric?.isResultArray ||
			this.rowDate?.isResultArray ||
			this.rowDimension?.isResultArray;
		if (rowThatIsObjectKey) {
			// output example
			// $keys(Account.Order).{
			// 	"date": $,
			// 	"value": $sum($lookup($$.Account.Order, $).Price),
			// 	"dimension": $join($lookup($$.Account.Order, $).Type, ' / ')
			// }

			const rowsThatAreNotObjectKey = rows.filter(
				(rowData) => rowData.type !== rowThatIsObjectKey.type,
			);

			// building string with keys function
			const pathToObjectKey = rowThatIsObjectKey.model.path,
				pathToObjectKeyWithObjectKey = pathToObjectKey
					? `${pathToObjectKey}.${rowThatIsObjectKey.model.escapedObjectKey}`
					: rowThatIsObjectKey.model.escapedObjectKey;
			string = `$keys(${pathToObjectKey || '$$'}).{\n`;

			const rowStrings: string[] = [];
			rowStrings.push(`\t"${getOutputPayloadKeyForRowType(rowThatIsObjectKey.type)}": $`);
			rowsThatAreNotObjectKey.forEach((rowData) => {
				let pathToRowWithoutPathToObjectKey = rowData.model.path;
				if (
					pathToObjectKeyWithObjectKey &&
					pathToRowWithoutPathToObjectKey.startsWith(pathToObjectKeyWithObjectKey)
				) {
					pathToRowWithoutPathToObjectKey = pathToRowWithoutPathToObjectKey.replace(
						`${pathToObjectKeyWithObjectKey}.`,
						'',
					);
				}
				let valueString = `$lookup($$${pathToObjectKey ? '.' + pathToObjectKey : ''}, $).${pathToRowWithoutPathToObjectKey}`;
				if (rowData.type === ApiMetricBuilderWizardStep.METRIC) {
					// add sum to metric
					valueString = `$sum(${valueString})`;
				}
				if (rowData.type === ApiMetricBuilderWizardStep.DIMENSION) {
					// concat attributes
					valueString = `$join(${valueString}. ' / ')`;
				}
				rowStrings.push(
					`\t"${getOutputPayloadKeyForRowType(rowData.type)}": ${valueString}`,
				);
			});
			string += rowStrings.join(',\n');
			string += '\n}';
		} else if (isResultArray) {
			// output example
			// sales.{
			// 	"value": mount,
			// 	"dimension": %.store,
			// 	"date": date
			// }

			// searching for deepest array parent for all selections (the one to iterate over)
			let arrayParent: JSONStructureRow | null = null;
			rows.forEach((rowData) => {
				// closest parent that is an array start
				let parentRow: JSONStructureRow | null = rowData.model;
				do {
					parentRow = parentRow?.parentRow || null;
				} while (parentRow && !parentRow.isArrayStart);

				if (parentRow?.isArrayStart) {
					if (!arrayParent || arrayParent.tabCount < parentRow.tabCount) {
						arrayParent = parentRow;
					}
				}
			});

			const deepestArrayParent = arrayParent as JSONStructureRow | null;

			if (deepestArrayParent) {
				let pathToDeepestParent = deepestArrayParent.path;
				if (pathToDeepestParent) {
					pathToDeepestParent += '.';
				}
				pathToDeepestParent += deepestArrayParent.escapedObjectKey;
				string = `${pathToDeepestParent || '$$'}.{\n`;

				const rowStrings: string[] = [];
				rows.forEach((rowData) => {
					const r = rowData.model;

					let parentRow: JSONStructureRow | null = r;
					do {
						parentRow = parentRow?.parentRow || null;
					} while (parentRow && parentRow !== deepestArrayParent);
					const isChildOfDeepestParent = parentRow && parentRow === deepestArrayParent;

					let valueString = '';
					if (isChildOfDeepestParent) {
						valueString = r.path;
						if (pathToDeepestParent) {
							valueString = valueString.replace(`${pathToDeepestParent}.`, '');
						}
					} else {
						let chunksNotMatching: string[] = [];
						r.pathChunks.some((chunk, k) => {
							const isSamePath = chunk === deepestArrayParent.pathChunks[k];
							if (!isSamePath) {
								chunksNotMatching = r.pathChunks.slice(k);
							}
							return !isSamePath;
						});

						if (chunksNotMatching.length === r.pathChunks.length) {
							// nothing matches - absolute path
							valueString = `$$.${r.path}`;
						} else {
							// relative, going up

							valueString = Array(r.pathChunks.length - chunksNotMatching.length)
								.fill('%')
								.join('.');
							valueString += '.' + chunksNotMatching.join('.');
						}
					}
					rowStrings.push(
						`\t"${getOutputPayloadKeyForRowType(rowData.type)}": ${valueString}`,
					);
				});

				string += rowStrings.join(',\n');
				string += `\n}`;
			}
		} else {
			// output example
			// {
			// 	"value": amount,
			// 	"dimension": store,
			// 	"date": date
			// }

			const rowStrings: string[] = [];
			string = `{\n`;
			rows.forEach((rowData) => {
				rowStrings.push(
					`\t"${getOutputPayloadKeyForRowType(rowData.type)}": ${rowData.model.path}`,
				);
			});
			string += rowStrings.join(',\n');
			string += `\n}`;
		}

		return string;
	}

	private createRows(): void {
		const rowsReverse: JSONStructureRow[] = [],
			rowStrings = this.jsonString.split('\n');

		rowStrings.forEach((rowString, idx) => {
			const row = new JSONStructureRow(this.json, rowString, idx, rowsReverse);
			row.parentRow = this.findParentRow(row, rowsReverse);
			// keeping reverse rows is an optimization - they are used for creating and finding parents
			rowsReverse.unshift(row);
		});

		this.rows = rowsReverse.reverse();
	}

	private findParentRow(
		row: JSONStructureRow,
		rowsReverse: JSONStructureRow[],
	): JSONStructureRow | null {
		let parentRow;

		// we have only previous rows here, start from bottom up
		rowsReverse.some((r) => {
			if (r.tabCount >= row.tabCount) {
				return false;
			}
			if (r.isArrayStart || r.isObjectKey) {
				parentRow = r;
				return true;
			}
			return false; // unsure if any case gets to this at all
		});

		return parentRow || null;
	}
}
